import React, { useMemo, useState } from "react";
import PlayerFormation from "./components/PlayerFormation";
import TeamPanel from "./components/TeamPanel";
import type { MatchStatus } from "service";
import { getTeamColors } from "components/colors";
import { parseLup } from "./helper";
import { buildRosterFromPly, TeamRoster } from "./utils";
import { ReactComponent as Court } from "../assets/football-court.svg";
import { Text } from "components/common/Text";

type LineupProps = { status: MatchStatus };

const Lineup: React.FC<LineupProps> = ({ status }) => {
  const [homeFormation, setHomeFormation] = useState<string>("4-4-2");
  const [awayFormation, setAwayFormation] = useState<string>("4-4-2");

  const rawLp: any = (status as any)?.lp || (status as any)?.c?.lp || undefined;
  const lupParsed = useMemo(() => parseLup((status as any)?.lup), [status]);

  const apiHome: TeamRoster | undefined = rawLp?.h
    ? buildRosterFromPly(rawLp.h, {
        starting: lupParsed.home.starting,
        subs: lupParsed.home.subs,
      })
    : undefined;

  const apiAway: TeamRoster | undefined = rawLp?.a
    ? buildRosterFromPly(rawLp.a, {
        starting: lupParsed.away.starting,
        subs: lupParsed.away.subs,
      })
    : undefined;

  const homePlayers: TeamRoster =
    apiHome && apiHome.starting.length + apiHome.substitutes.length > 0
      ? apiHome
      : { starting: [], substitutes: [] };

  const awayPlayers: TeamRoster =
    apiAway && apiAway.starting.length + apiAway.substitutes.length > 0
      ? apiAway
      : { starting: [], substitutes: [] };

  const { home, away } = getTeamColors(status);

  return (
    <div className="flex w-full flex-col">
      <div className="relative h-[45vw] w-full">
        <Court className="absolute inset-0 h-full w-full select-none" />
        <PlayerFormation
          homeFormation={homeFormation}
          awayFormation={awayFormation}
          status={status}
        />
      </div>

      <div className="block p-[1vw]">
        {/* STARTING XI */}
        <SectionHeading>Starting XI</SectionHeading>
        <div className="grid grid-cols-2 gap-[2vw]">
          <TeamPanel
            teamType="home"
            players={homePlayers}
            formation={homeFormation}
            onFormationChange={setHomeFormation}
            only="starting"
            hideSectionHeaders
            homeColor={home}
            awayColor={away}
          />
          <TeamPanel
            teamType="away"
            players={awayPlayers}
            formation={awayFormation}
            onFormationChange={setAwayFormation}
            only="starting"
            hideSectionHeaders
            homeColor={home}
            awayColor={away}
          />
        </div>

        {/* SUBSTITUTES */}
        <SectionHeading>Substitutes</SectionHeading>
        <div className="grid grid-cols-2 gap-[2vw]">
          <TeamPanel
            teamType="home"
            players={homePlayers}
            formation={homeFormation}
            onFormationChange={setHomeFormation}
            only="substitutes"
            hideSectionHeaders
            homeColor={home}
            awayColor={away}
          />
          <TeamPanel
            teamType="away"
            players={awayPlayers}
            formation={awayFormation}
            onFormationChange={setAwayFormation}
            only="substitutes"
            hideSectionHeaders
            homeColor={home}
            awayColor={away}
          />
        </div>
      </div>
    </div>
  );
};

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="my-[2vw] flex items-center justify-center gap-[1.2vw] text-center font-extrabold text-white">
    <span className="hidden h-[0.2vw] flex-1 rounded-full md:block bg-lightGray" />
    <Text variant="soccerLineupTitle">{children}</Text>
    <span className="hidden h-[0.2vw] flex-1 rounded-full md:block bg-lightGray" />
  </div>
);

export default Lineup;
